import { graphqlPost } from "api/graphqlClient";
import restClient from "api/restClient";
import { Autocomplete } from "components/Form/components/Autocomplete/Autocomplete";
import { Text } from "components/Form/components/Text/Text";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import { useMigrateOnSaveParams } from "containers/Resources/screens/ResourcesSingle/useCanCommitOnSave";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getSearchObj } from "utils/utils";

/**
 * Bundle-only settings block for the "Container settings" dialog: the bundle's
 * `version` (free text), `license` (SPDX identifier or expression) and
 * `bundle_repository` (reference picker).
 *
 * Per Stefan's 2026-09-17 target design these controls live OUT of the
 * container screen body (they used to be generic `form` fields on the bundle
 * resource config) and INSIDE this dialog, so the container screen is not
 * cluttered with rarely-touched settings. Only bundle containers render it —
 * projects and workspaces have no version / license / repository.
 *
 * Edits apply live (Stefan's "apply immediately without needing to refresh"):
 * `version` and `license` save on blur / Enter, `bundle_repository` saves on
 * selection. All go through a single `update_bundle` mutation that always sends
 * every current value so no field clobbers another. A non-empty `license` that
 * is not a valid SPDX expression is rejected by the backend (HTTP 400) and the
 * error surfaces via the shared RestErrorNotification. The record subscription that backs
 * the container screen picks the change up and re-renders the header without a
 * page refresh. Mirrors the raw-GraphQL save pattern of
 * {@link ContainerDashboardManager} (commit:false + migrate-on-save params).
 */
export const BundleSettings = ({
    recordId,
    isDisabled,
}: {
    recordId?: string;
    isDisabled?: boolean;
}) => {
    const [version, setVersion] = useState<string>("");
    const [license, setLicense] = useState<string>("");
    const [repositoryId, setRepositoryId] = useState<string | null>(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const migrateOnSaveParams = useMigrateOnSaveParams();
    const params = useMemo(() => ({ commit: false, ...migrateOnSaveParams }), [migrateOnSaveParams]);

    const fetchBundle = useCallback(() => {
        if (!recordId) {
            return Promise.resolve();
        }
        const filter = JSON.stringify(getSearchObj({ field: "id", op: "eq", value: recordId }));
        const query = `query BundleSettings($filter: String) {
            bundle_plain_list(filter: $filter) {
                id
                version
                license
                bundle_repository_id
            }
        }`;
        return graphqlPost({ query, variables: { filter } })
            .then((response) => {
                const bundle = response?.data?.bundle_plain_list?.[0];
                if (bundle) {
                    setVersion(bundle.version ?? "");
                    setLicense(bundle.license ?? "");
                    setRepositoryId(bundle.bundle_repository_id ?? null);
                }
                setIsLoaded(true);
            })
            .catch(() => setIsLoaded(true));
    }, [recordId]);

    useEffect(() => {
        fetchBundle();
    }, [fetchBundle]);

    // Persist both fields together so a single-field edit never nulls the other.
    const save = useCallback(
        (
            actionLabel: string,
            nextVersion: string,
            nextRepositoryId: string | null,
            nextLicense: string
        ) => {
            setIsSaving(true);
            return restClient
                .post(
                    "/graphql",
                    {
                        query: `mutation update_bundle($id: String!, $version: String, $bundle_repository_id: String, $license: String) {
                            update_bundle(body: {version: $version, bundle_repository_id: $bundle_repository_id, license: $license}, id: $id) {
                                id
                            }
                        }`,
                        variables: {
                            id: recordId,
                            version: nextVersion,
                            bundle_repository_id: nextRepositoryId,
                            license: nextLicense,
                        },
                    },
                    { params }
                )
                .catch((error) =>
                    NotificationService.error(
                        <RestErrorNotification
                            titleOverride={`Sorry, couldn't ${actionLabel}`}
                            error={error}
                        />
                    )
                )
                .finally(() => setIsSaving(false));
        },
        [recordId, params]
    );

    if (!recordId) {
        return null;
    }

    const canEdit = !isDisabled;

    return (
        <div className="bundleSettings">
            <h3 className="bundleSettings__heading">Bundle</h3>

            <label className="bundleSettings__field" htmlFor="bundleSettings-version">
                <span className="bundleSettings__label">Version</span>
                <Text
                    name="bundleSettings-version"
                    inputProps={{
                        value: version,
                        isReadOnly: !canEdit,
                        onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                            setVersion(event.target.value),
                        onBlur: () => {
                            if (canEdit && isLoaded) {
                                save("update the version", version, repositoryId, license);
                            }
                        },
                    }}
                    onEnterKey={(currentValue: string) => {
                        if (canEdit && isLoaded) {
                            save("update the version", currentValue, repositoryId, license);
                        }
                    }}
                />
            </label>

            <label className="bundleSettings__field" htmlFor="bundleSettings-license">
                <span className="bundleSettings__label">License</span>
                <Text
                    name="bundleSettings-license"
                    inputProps={{
                        value: license,
                        isReadOnly: !canEdit,
                        onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                            setLicense(event.target.value),
                        onBlur: () => {
                            if (canEdit && isLoaded) {
                                save("update the license", version, repositoryId, license);
                            }
                        },
                    }}
                    onEnterKey={(currentValue: string) => {
                        if (canEdit && isLoaded) {
                            save("update the license", version, repositoryId, currentValue);
                        }
                    }}
                />
            </label>

            <div className="bundleSettings__field">
                <span className="bundleSettings__label">Bundle repository</span>
                <Autocomplete
                    name="bundleSettings-bundle-repository"
                    options={{ resource: "bundle_repository" }}
                    value={repositoryId}
                    isDisabled={!canEdit || isSaving}
                    onChange={(selectedValue: any) => {
                        const nextRepositoryId = selectedValue ?? null;
                        setRepositoryId(nextRepositoryId);
                        if (canEdit && isLoaded) {
                            save("update the bundle repository", version, nextRepositoryId, license);
                        }
                    }}
                />
            </div>
        </div>
    );
};

export default BundleSettings;
